import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import {
  DateRange,
  DateRangePicker,
} from "../core/components/date-range-picker";
import BasePage from "../core/base.page";
import HeadingComponent from "../core/components/heading.component";
import { defaultRanges } from "../core/components/date-range-picker/defaults";
import { useAuth } from "../core/context/auth.context";
import StatsService from "../core/service/stats.service";
import { useQuery } from "@tanstack/react-query";
import GameResultInterface from "@homegame/common/dist/interface/game-result.interface";
import TableChartCard from "./table-chart.component";
import EmptyStateCard from "../core/components/empty-state-card.component";
import CasinoIcon from "@mui/icons-material/Casino";
import DateRangeControl from "../core/components/date-range-picker/components/DateRangeControl";

export interface TableChartData {
  tableId: number;
  tableName: string;
  results: ChartGameResult[];
}

export interface ChartGameResult {
  date: string;
  result: number;
}

const useStyles = makeStyles(() =>
  createStyles({
    container: {
      maxWidth: 500,
      margin: "0 auto",
      textAlign: "center",
      paddingBottom: 120,
      "&:after": {
        backgroundImage: "url(/cards.svg)",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundSize: "70%",
        content: '""',
        display: "block",
        position: "absolute",
        width: "100%",
        height: "100%",
        filter: "opacity(0.1)",
        top: 0,
        left: 0,
        zIndex: -1,
      },
    },
    btn: {
      border: "1px solid #fff",
      color: "#fff",
      "& .MuiButtonBase-root": { color: "#fff" },
      "& .MuiButton-root": {
        color: "#fff",
        fontSize: 13,
        textTransform: "none",
        "& .MuiSvgIcon-root": {
          marginRight: 10,
          marginLeft: 5,
        },
      },
    },
    chartCanvas: {},
    chartwrapper: {},
    noItemsText: {
      textAlign: "center",
      fontSize: 14,
      display: "block",
      color: "#fff",
      fontWeight: 400,
      position: "absolute",
      top: "50%",
      width: "100%",
      textShadow: "none",
    },
  }),
);

export default function StatsPage() {
  const classes = useStyles({});

  const statsService = new StatsService();

  const { user } = useAuth();

  const [open, setOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>(defaultRanges[2]);

  const [groupedData, setGroupedData] = useState<TableChartData[]>([]);

  const toggle = () => setOpen(!open);

  const getDatesString = () => {
    const { label, startDate, endDate } = dateRange;
    if (label) return label;
    if (!startDate || !endDate) return "Select date";
    return `${startDate.format("DD MMM")} - ${endDate.format("DD MMM")}`;
  };

  const prevRange = () => {
    const { startDate, endDate } = dateRange;
    if (!startDate || !endDate) return;
    const daysDiff = startDate!.diff(endDate, "days");
    setDateRange({
      startDate: startDate.subtract(daysDiff, "days"),
      endDate: endDate.subtract(daysDiff, "days"),
    });
  };

  const nextRange = () => {
    const { startDate, endDate } = dateRange;
    if (!startDate || !endDate) return;
    const daysDiff = startDate!.diff(endDate, "days");
    setDateRange({
      startDate: startDate.add(daysDiff, "days"),
      endDate: endDate.add(daysDiff, "days"),
    });
  };

  const changeRange = (range: DateRange) => {
    setDateRange(range);
    toggle();
  };

  const clearDate = () => {
    setDateRange(defaultRanges[2]);
  };

  const { data, refetch } = useQuery({
    queryKey: [`singleUserData`, { userId: user!.id }],
    queryFn: () => {
      if (!dateRange.startDate || !dateRange.endDate) return;
      const startDate = dateRange.startDate.startOf("day");
      const endDate = dateRange.endDate.endOf("day");
      return statsService.userStatByWinLoseMoney(user!.id!, {
        dateFrom: startDate.format(),
        dateTo: endDate.format(),
      });
    },
    initialData: [],
  });

  const groupByTableAndDate = (
    data: GameResultInterface[],
  ): TableChartData[] => {
    const tableMap = new Map<number, TableChartData>();

    data.forEach((item) => {
      if (!item.game?.table) return;

      const { id: tableId, name: tableName } = item.game.table;
      const date = item.game.date.toString().split("T")[0]; // yyyy-mm-dd

      if (!tableMap.has(Number(tableId))) {
        tableMap.set(Number(tableId), {
          tableId: Number(tableId),
          tableName,
          results: [],
        });
      }

      const table = tableMap.get(Number(tableId))!;

      const dateResult = table.results.find((r) => r.date === date);

      if (dateResult) {
        dateResult.result += item.result;
      } else {
        table.results.push({
          date,
          result: item.result,
        });
      }
    });

    tableMap.forEach((table) => {
      table.results.sort((a, b) => a.date.localeCompare(b.date));
    });

    return Array.from(tableMap.values());
  };

  useEffect(() => {
    if (!data || !Array.isArray(data)) return;
    setGroupedData(groupByTableAndDate(data));
  }, [data]);

  useEffect(() => {
    refetch();
  }, [dateRange]);

  return (
    <BasePage>
      <Grid
        container
        spacing={0}
        direction="row"
        alignItems="flex-start"
        className={classes.container}
      >
        <Grid item xs={12}>
          <HeadingComponent text="Stats" />
        </Grid>
        <Grid item sm={12} xs={12} mt={2}>
          <DateRangeControl
            label={getDatesString()}
            onPrev={prevRange}
            onNext={nextRange}
            onClear={clearDate}
            onDateClick={() => setOpen(true)}
          />
        </Grid>

        <Grid
          item
          xs={12}
          style={{ position: "relative" }}
          className={classes.chartwrapper}
          mt={2}
        >
          {groupedData.length === 0 ? (
            <EmptyStateCard message="No games found" Icon={CasinoIcon} />
          ) : (
            groupedData.map((table) => (
              <TableChartCard key={table.tableId} table={table} />
            ))
          )}
        </Grid>
      </Grid>
      <DateRangePicker open={open} toggle={toggle} onChange={changeRange} />
    </BasePage>
  );
}
